import React, { useEffect, useState, useCallback } from "react";
import "./Toast.css";

interface ToastAction {
  text: string;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

interface ToastProps {
  message: string;
  type: "success" | "error" | "info";
  isVisible: boolean;
  onClose: () => void;
  duration?: number;
  action?: ToastAction;
}

const Toast: React.FC<ToastProps> = ({
  message,
  type,
  isVisible,
  onClose,
  duration = 3000,
  action,
}) => {
  const [isFadingOut, setIsFadingOut] = useState(false);

  const handleClose = useCallback(() => {
    setIsFadingOut(true);
    setTimeout(onClose, 500);
  }, [onClose]);

  useEffect(() => {
    if (isVisible) {
      setIsFadingOut(false);
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, handleClose]);

  const handleActionClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (action) {
      action.onClick(e);
      handleClose();
    }
  };

  if (!isVisible && !isFadingOut) return null;

  return (
    <div
      className={`toast ${type} ${isFadingOut ? "fade-out" : "fade-in"} ${
        action ? "with-action" : ""
      }`}
    >
      <div className="toast-content">
        <span className="toast-icon">
          {type === "success" && "✅"}
          {type === "error" && "❌"}
          {type === "info" && "ℹ️"}
        </span>
        <span className="toast-message">{message}</span>
        {action && (
          <button onClick={handleActionClick} className="toast-action">
            {action.text}
          </button>
        )}
        <button className="toast-close" onClick={handleClose}>
          ×
        </button>
      </div>
    </div>
  );
};

export default Toast;
